/**
 * A small text editor ([data-rich-editor]) with bold, italic and bulleted and numbered
 * lists, for a <textarea> that keeps the HTML the form sends.
 *
 * Without JavaScript the textarea is an ordinary text box. With it, the textarea is
 * hidden and the server-rendered toolbar and editing area are shown; the area's HTML
 * goes back into the textarea on every change. Pasted or dropped content arrives as
 * plain text, so formatting copied from Word or a web page does not come through, and
 * the HTML put back into the area (after a server error) is first reduced to the same
 * few tags. The server cleans what it receives again.
 */
const ALLOWED = { B: 'strong', STRONG: 'strong', I: 'em', EM: 'em', UL: 'ul', OL: 'ol', LI: 'li', P: 'p', DIV: 'p', BR: 'br' };

/** Rebuild an HTML string with only the tags above and no attributes. */
export function cleanHtml(html) {
    const source = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html').body;
    const out = document.createElement('div');
    const copy = (from, to) => {
        from.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                to.append(node.textContent);
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const tag = ALLOWED[node.tagName];
                if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') {
                    return;
                }
                if (tag) {
                    const el = document.createElement(tag);
                    to.append(el);
                    copy(node, el);
                } else {
                    copy(node, to);
                }
            }
        });
    };
    copy(source, out);
    return out.innerHTML;
}

const isEmpty = (area) => area.textContent.trim() === '' && !area.querySelector('li');

function initEditor(root) {
    const textarea = root.querySelector('textarea');
    const toolbar = root.querySelector('[data-editor-toolbar]');
    const area = root.querySelector('[data-editor-area]');
    if (!textarea || !toolbar || !area || typeof document.execCommand !== 'function') {
        return;
    }

    root.classList.add('is-enhanced');
    if (textarea.required) {
        root.dataset.required = '1';
    }
    textarea.required = false;
    textarea.hidden = true;
    toolbar.hidden = false;
    area.hidden = false;
    area.contentEditable = 'true';
    area.innerHTML = cleanHtml(textarea.value);

    const buttons = [...toolbar.querySelectorAll('button[data-command]')];

    const save = () => {
        textarea.value = isEmpty(area) ? '' : cleanHtml(area.innerHTML);
        root.classList.remove('is-invalid');
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const refresh = () => {
        const inside = area.contains(document.getSelection()?.anchorNode || null);
        buttons.forEach((button) => {
            let on = false;
            try {
                on = inside && document.queryCommandState(button.dataset.command);
            } catch (e) {
                on = false;
            }
            button.setAttribute('aria-pressed', String(!!on));
        });
    };

    buttons.forEach((button, index) => {
        // Keep the selection in the area when a button is clicked.
        button.addEventListener('mousedown', (event) => event.preventDefault());
        button.addEventListener('click', () => {
            if (area.contentEditable !== 'true' || textarea.disabled) {
                return;
            }
            area.focus();
            document.execCommand(button.dataset.command, false, null);
            save();
            refresh();
        });
        // One tab stop for the toolbar; the arrow keys move between its buttons.
        button.tabIndex = index === 0 ? 0 : -1;
        button.addEventListener('keydown', (event) => {
            const moves = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: buttons.length - 1 - index };
            if (!(event.key in moves)) {
                return;
            }
            event.preventDefault();
            const next = buttons[(index + moves[event.key] + buttons.length) % buttons.length];
            buttons.forEach((b) => { b.tabIndex = b === next ? 0 : -1; });
            next.focus();
        });
    });

    const insertPlain = (value) => {
        if (value) {
            document.execCommand('insertText', false, value);
        }
    };
    area.addEventListener('paste', (event) => {
        event.preventDefault();
        insertPlain(event.clipboardData?.getData('text/plain') || '');
    });
    area.addEventListener('drop', (event) => {
        event.preventDefault();
        const value = event.dataTransfer?.getData('text/plain') || '';
        if (value && document.caretRangeFromPoint) {
            const range = document.caretRangeFromPoint(event.clientX, event.clientY);
            const selection = document.getSelection();
            if (range && selection) {
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
        insertPlain(value);
    });
    area.addEventListener('input', save);
    area.addEventListener('keyup', refresh);
    area.addEventListener('mouseup', refresh);
    area.addEventListener('focus', refresh);
    document.addEventListener('selectionchange', refresh);

    try {
        document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch (e) {
        // Older browsers keep their own separator.
    }

    // The editing area follows its textarea when the form disables it.
    const lock = () => {
        area.contentEditable = textarea.disabled ? 'false' : 'true';
        area.setAttribute('aria-disabled', String(textarea.disabled));
    };
    new MutationObserver(lock).observe(textarea, { attributes: true, attributeFilter: ['disabled'] });
    lock();

    // The label points at the hidden textarea: a click on it goes to the editing area.
    if (textarea.id) {
        root.querySelector(`label[for="${textarea.id}"]`)?.addEventListener('click', (event) => {
            event.preventDefault();
            area.focus();
        });
    }

    root.closest('form')?.addEventListener('submit', save, true);

    root.richEditor = { isEmpty: () => isEmpty(area), focus: () => area.focus(), save };
}

export function initRichEditors(root = document) {
    root.querySelectorAll('[data-rich-editor]:not([data-editor-ready])').forEach((el) => {
        el.dataset.editorReady = '1';
        initEditor(el);
    });
}
